import { motion } from 'framer-motion';
import { ChevronRight, Play, Star, ThumbsUp, Users } from 'lucide-react';
import { projects } from '../data/projects';
import { useLanguage } from '../i18n/LanguageContext';
import { copy } from '../i18n/copy';

export default function FeaturedProjects() {
  const { t } = useLanguage();

  return (
    <section id="projects" className="mx-auto max-w-6xl scroll-mt-24 px-5 py-16">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.4 }}
        transition={{ duration: 0.5 }}
        className="flex flex-wrap items-center justify-between gap-3"
      >
        <h2 className="inline-flex items-center gap-2.5 text-2xl font-extrabold text-white md:text-3xl">
          <Star className="size-6 fill-current text-white" />
          {t(copy.projectsTitle)}
        </h2>
        <a
          href="/src/client/pages/dashboard.html"
          className="inline-flex items-center gap-1 text-sm font-semibold text-sky-400 transition-colors hover:text-sky-300"
        >
          {t(copy.viewAllProjects)}
          <ChevronRight className="size-4 rtl:rotate-180" />
        </a>
      </motion.div>

      <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {projects.map((project, i) => (
          <motion.article
            key={project.id}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.45, delay: i * 0.08 }}
            whileHover={{ y: -6 }}
            className="group overflow-hidden rounded-3xl border border-white/10 bg-navy-900/70 backdrop-blur-xl transition-colors duration-300 hover:border-sky-400/40"
          >
            {/* Thumbnail */}
            <div className="relative h-40 overflow-hidden">
              <img
                src={project.image}
                alt={t(project.title)}
                loading="lazy"
                className="absolute inset-0 size-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
              <span
                className={`absolute start-3 top-3 rounded-md px-2.5 py-1 text-[11px] font-bold text-white ${project.tagClass}`}
              >
                {t(project.tag)}
              </span>
              <span className="absolute end-3 top-1/2 grid size-10 -translate-y-1/2 place-items-center rounded-full bg-black/50 ring-1 ring-white/30 backdrop-blur transition-transform duration-300 group-hover:scale-110">
                <Play className="size-4 fill-current text-white" />
              </span>
            </div>

            <div className="p-5">
              <h3 className="font-bold text-white">{t(project.title)}</h3>
              <p className="mt-1.5 text-sm leading-relaxed text-slate-400">
                {t(project.description)}
              </p>
              <div className="mt-4 flex items-center gap-4 text-xs text-slate-400">
                <span className="inline-flex items-center gap-1.5">
                  <ThumbsUp className="size-3.5" />
                  {project.stats.likes}
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <Users className="size-3.5" />
                  {project.stats.users}
                </span>
              </div>
            </div>
          </motion.article>
        ))}
      </div>
    </section>
  );
}
